import type { JourneyResponse } from '@manasil/shared';
import { COVER_IMAGE, goalEmoji } from '@manasil/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { resolveMediaUrl } from '../lib/api.js';
import {
  COVER_SPEC,
  IMAGE_ACCEPT,
  type ImageCheck,
  inspectImage,
  removeJourneyCover,
  uploadJourneyCover,
} from '../lib/image.js';

/**
 * The journey cover.
 *
 * Everything here exists to stop a wrong image reaching the bucket. The file is measured in
 * the browser first, and an editor is told what is wrong while they still have the original
 * to hand — a warning after the upload is a warning they cannot act on.
 *
 * The preview is deliberately the app's own shape and carries the app's own scrim, because
 * the single most common mistake is a cover whose subject sits in the bottom third where
 * the title bar buries it. Describing that in a spec does not work; showing it does.
 */
export function CoverCard({ journey }: { journey: JourneyResponse }) {
  const client = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [pending, setPending] = useState<{ file: File; check: ImageCheck } | null>(null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // An object URL is a live handle to the file; letting them pile up leaks the file itself.
  useEffect(() => {
    return () => {
      if (pending) URL.revokeObjectURL(pending.check.previewUrl);
    };
  }, [pending]);

  async function choose(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError(null);
    try {
      const check = await inspectImage(file, COVER_SPEC);
      if (pending) URL.revokeObjectURL(pending.check.previewUrl);
      setPending({ file, check });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not read that image');
    }
  }

  async function upload() {
    if (!pending) return;
    setBusy(true);
    setError(null);
    try {
      await uploadJourneyCover(journey.id, pending.file, pending.check, setProgress);
      await client.invalidateQueries({ queryKey: ['journey', journey.id] });
      URL.revokeObjectURL(pending.check.previewUrl);
      setPending(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Upload failed');
    } finally {
      setBusy(false);
      setProgress(0);
    }
  }

  async function clear() {
    setBusy(true);
    try {
      await removeJourneyCover(journey.id);
      await client.invalidateQueries({ queryKey: ['journey', journey.id] });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not remove the cover');
    } finally {
      setBusy(false);
    }
  }

  /**
   * A blob: URL for a file not yet uploaded, otherwise the stored cover — which arrives as
   * a RELATIVE signed path from the local storage driver and would otherwise be resolved
   * against the portal's own origin rather than the API's.
   */
  const shown = pending
    ? pending.check.previewUrl
    : journey.coverUrl
      ? resolveMediaUrl(journey.coverUrl)
      : null;
  const blocked = (pending?.check.problems.length ?? 0) > 0;

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h3>Cover</h3>
          <p className="hint">
            {COVER_IMAGE.width} × {COVER_IMAGE.height} (2:1), JPEG, 150–300 KB. The title now sits
            below the image on both screens, so the whole 2:1 frame is visible — nothing to compose
            around.
          </p>
        </div>
        {journey.coverUrl && !pending ? (
          <button type="button" className="btn btn-ghost btn-sm" onClick={clear} disabled={busy}>
            Remove
          </button>
        ) : null}
      </div>

      {error ? <div className="banner">{error}</div> : null}

      <div className="cover-previews">
        {/* A phone, matching the hero card's frame, showing the journey preview screen the
            cover actually appears on: the back row, the 170pt panel inset 24pt a side with
            the goal tile and title over its foot, and the blurb starting underneath. Same
            390 x 430 proportions as the hero, so the two cards are the same height and can
            be compared directly. */}
        <div className="cover-phone" aria-label="How this cover looks on the journey preview">
          <div className="cover-head">
            <span className="cover-chip">←</span>
            <span className="cover-crumb">Journey Library · Preview</span>
          </div>

          <div className="cover-panel">
            {shown ? (
              <img src={shown} alt="" className="cover-img" />
            ) : (
              <div className="cover-empty">No cover — the app falls back to the goal's colour</div>
            )}
          </div>

          {/* Under the panel, not over it — and with no scrim, because nothing covers the
              image any more. */}
          <div className="cover-bar">
            <div className="cover-bar-tile">{goalEmoji(journey.goalKeys[0] ?? '')}</div>
            <div>
              <div className="cover-bar-title">{journey.title}</div>
              <div className="cover-bar-meta">
                {journey.durationDays} Days Journey · {journey.dayCount} stories
              </div>
            </div>
          </div>

          {/* Where the panel ends and the journey's description begins. */}
          <div className="cover-body">
            <span className="cover-line" style={{ width: '100%' }} />
            <span className="cover-line" style={{ width: '88%' }} />
            <span className="cover-line" style={{ width: '62%' }} />
          </div>

          <span className="cover-tag">In the app</span>
        </div>
      </div>

      {pending ? (
        <div className="stack" style={{ gap: 8, marginTop: 12 }}>
          <div className="hint nums">
            {pending.check.width} × {pending.check.height} · {pending.check.aspect.toFixed(2)}:1 ·{' '}
            {Math.round(pending.file.size / 1024)} KB
          </div>
          {pending.check.problems.map((problem) => (
            <div key={problem} className="banner">
              {problem}
            </div>
          ))}
          {pending.check.warnings.map((warning) => (
            <div key={warning} className="hint">
              {warning}
            </div>
          ))}
          {busy ? <progress value={progress} max={1} style={{ width: '100%' }} /> : null}
          <div className="row" style={{ gap: 9 }}>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={upload}
              disabled={busy || blocked}
              title={blocked ? 'Fix the problems above first' : undefined}
            >
              {busy ? `Uploading ${Math.round(progress * 100)}%` : 'Use this cover'}
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                URL.revokeObjectURL(pending.check.previewUrl);
                setPending(null);
              }}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="row" style={{ gap: 9, marginTop: 12 }}>
          <button
            type="button"
            className="btn btn-sm"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
          >
            {journey.coverUrl ? 'Replace cover' : 'Choose cover'}
          </button>
          {journey.coverWidth ? (
            <span className="hint nums">
              {journey.coverWidth} × {journey.coverHeight}
            </span>
          ) : null}
        </div>
      )}

      <input ref={fileRef} type="file" accept={IMAGE_ACCEPT} hidden onChange={choose} />
    </div>
  );
}
